import {
	type FC,
	type PropsWithChildren,
	useCallback,
	useEffect,
	useState,
} from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import NodeController05Abi from "@abi/NodeController05";
import AlertBox from "@components/AlertBox";
import useAccount from "@hooks/useAccount";
import type { Node } from "@hooks/useAllNodes";
import useContract from "@hooks/useContract";
import useTokenBalance from "@hooks/useTokenBalance";
import type { MyLocale } from "@i18n";
import { useAppSelector } from "@store/hooks";
import { contractMigrationRequired } from "@store/selectors/contract";
import formatRewards from "@utils/formatRewards";
import { useI18n } from "next-rosetta";
import { type Address, parseEther } from "viem";
import { useSimulateContract, useWriteContract } from "wagmi";

interface DepositProps {
	open?: boolean;
	// eslint-disable-next-line no-unused-vars
	setOpen: (value: boolean) => void;
	node: Node;
}

const useDeposit = (nodeId: bigint, amount: bigint) => {
	const { nodeController05Contract } = useContract();
	const { data } = useSimulateContract({
		abi: NodeController05Abi,
		address: nodeController05Contract.address as Address,
		args: [nodeId, amount],
		functionName: "increaseNodeAmount",
	});

	const {
		isPending: isLoading,
		reset,
		status,
		writeContract,
	} = useWriteContract();
	const state = status;
	const write = useCallback(() => {
		if (data?.request) {
			return writeContract(data?.request);
		}
	}, [data]);
	return { isLoading, resetState: reset, send: write, state, status };
};

const Deposit: FC<PropsWithChildren<DepositProps>> = ({
	node,
	open,
	setOpen,
}) => {
	const i18n = useI18n<MyLocale>();
	const { t } = i18n;
	const [value, setValue] = useState("100");
	const { account } = useAccount();
	const amount = parseEther(value.toString()).toString();
	const {
		isLoading,
		send: deposit,
		status,
	} = useDeposit(BigInt(node.__id ?? 0), BigInt(amount ?? 0));
	const { tokenBalance, tokenBalanceFormatted } = useTokenBalance(account);
	const isLowBalance = tokenBalance
		? Number(tokenBalanceFormatted) < Number(value)
		: true;
	const [confirmOpen, setConfirmOpen] = useState(false);
	const isMigrationRequired = useAppSelector(contractMigrationRequired);
	const handleCancelConfirm = () => {
		setConfirmOpen(false);
	};

	const handleClose = useCallback(() => {
		setOpen(false);
	}, [setOpen]);

	const handleChange = (event) => {
		setValue(event.target.value);
	};

	const minPendingConfirmPct = 0.25;
	const handleDeposit = () => {
		const diff = +node.rewards?.toString() / +node.amount?.toString();
		if (diff > minPendingConfirmPct / 100) {
			setConfirmOpen(true);
		} else {
			_handleDeposit();
		}
	};

	const _handleDeposit = () => {
		deposit();
		setConfirmOpen(false);
	};

	useEffect(() => {
		if (status && status === "success") {
			handleClose();
		}
	}, [handleClose, status]);

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				PaperProps={{
					sx: {
						backgroundImage: "unset",
						borderRadius: 5,
					},
				}}
			>
				<DialogTitle>{t("modals.deposit.title")}</DialogTitle>
				<DialogContent sx={{ minWidth: 280 }}>
					<FormControl fullWidth sx={{ marginTop: 2 }}>
						<TextField
							autoFocus
							margin="dense"
							id="Value"
							label={t("modals.deposit.label")}
							type="number"
							fullWidth
							variant="standard"
							value={value}
							helperText={t("modals.deposit.helperText", {
								balance: tokenBalanceFormatted || 0,
							})}
							onChange={handleChange}
							required
						/>
					</FormControl>
				</DialogContent>
				<DialogActions
					sx={{
						justifyContent: "space-between",
						paddingBottom: 4,
						px: 4,
					}}
				>
					<Button onClick={handleClose} variant="outlined" color="error">
						{t("modals.deposit.cancel")}
					</Button>
					<Stack direction="row" spacing={2}>
						<LoadingButton
							variant="contained"
							color="primary"
							onClick={handleDeposit}
							disabled={isLowBalance}
							loading={isLoading}
						>
							{t("modals.deposit.deposit")}
						</LoadingButton>
					</Stack>
				</DialogActions>
			</Dialog>
			<Dialog open={confirmOpen}>
				<DialogTitle>{t("modals.deposit.confirmTitle")}</DialogTitle>
				<DialogContent dividers>
					<AlertBox>
						{t("modals.deposit.pendingRewards", {
							pendingRewards: formatRewards(node.rewards),
						})}
					</AlertBox>
				</DialogContent>
				<DialogActions
					sx={{
						justifyContent: "space-between",
						paddingBottom: 4,
						px: 4,
					}}
				>
					<Button
						onClick={handleCancelConfirm}
						variant="outlined"
						color="error"
					>
						{t("modals.deposit.cancel")}
					</Button>
					<Stack direction="row" spacing={2}>
						<Button
							variant="contained"
							color="primary"
							onClick={_handleDeposit}
							disabled={isMigrationRequired}
						>
							{t("modals.deposit.deposit")}
						</Button>
					</Stack>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default Deposit;
